import { useMemo, useState } from 'react';
import { buildBranchingChanges, dcrBranchingImpact, dcrBranchingStepLabels, dcrBranchingStepsFromFlow, dcrFlowPath, dcrScreenLabels, dcrScreensFromFlow, findUnansweredBranchingSteps, initialBranchingSelections, } from '../../utils';
import { useDcrFlow } from '../use_dcr_field_options';
const isEmptyValue = (value) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
/**
 * Works out which screens a proposed value moves the activity to or away from. The path under the
 * recorded answers comes from the connector; the path under the proposal replays locally with the
 * recorded answers plus the user's edits. Only the difference between the two is worth showing:
 * the screens that lose their response and the screens that now need one.
 */
export const useDcrBranchingLogic = ({ context, taskResponseId, selectedScreen, proposedValue, savedSelections, }) => {
    const isBranchingScreen = !!taskResponseId && !!selectedScreen?.hasBranchingLogic;
    // The screen list has already loaded this, so this reads the same query rather than a second one
    const query = useDcrFlow(context, taskResponseId, isBranchingScreen);
    const flow = query.data;
    const steps = useMemo(() => (isBranchingScreen ? dcrBranchingStepsFromFlow(flow) : []), [isBranchingScreen, flow]);
    const screens = useMemo(() => (isBranchingScreen ? dcrScreensFromFlow(flow) : []), [isBranchingScreen, flow]);
    /**
     * Answers are keyed by the set of steps they belong to, so picking another screen or activity
     * falls back to the recorded answers instead of carrying the previous path's edits over.
     */
    const signature = steps.map((step) => step.stepId).join('|');
    const [edits, setEdits] = useState({
        signature: '',
        values: {},
    });
    const selections = useMemo(() => {
        const saved = { ...initialBranchingSelections(steps), ...savedSelections };
        return edits.signature === signature ? { ...saved, ...edits.values } : saved;
    }, [signature, edits, steps, savedSelections]);
    /**
     * The proposed value lives in the form's own "New value" field, so it is overlaid here rather
     * than collected twice. An empty proposal changes nothing, which keeps the impacted section
     * cleared until there is a value to replay with.
     */
    const effectiveSelections = useMemo(() => {
        if (!selectedScreen || isEmptyValue(proposedValue)) {
            return selections;
        }
        return { ...selections, [String(selectedScreen.stepId)]: proposedValue };
    }, [selections, selectedScreen, proposedValue]);
    const pathStepIds = useMemo(() => new Set(dcrFlowPath(flow?.screens ?? [], effectiveSelections)), [flow?.screens, effectiveSelections]);
    const path = useMemo(() => steps.filter((step) => pathStepIds.has(step.stepId)), [steps, pathStepIds]);
    /** The path the participant actually took, as replayed by the connector from the recorded answers */
    const recordedPathStepIds = useMemo(() => new Set((flow?.recordedPath ?? dcrFlowPath(flow?.screens ?? [], {})).map(String)), [flow]);
    const impact = useMemo(() => dcrBranchingImpact(steps, path, recordedPathStepIds, pathStepIds), [steps, path, recordedPathStepIds, pathStepIds]);
    const hasDiff = impact.unreachableSteps.length > 0 || impact.newlyReachableSteps.length > 0;
    /**
     * Questions can quote an earlier answer, so a title has to be rebuilt whenever an answer above
     * it changes rather than resolved once when the activity loads.
     */
    const labels = useMemo(() => dcrBranchingStepLabels({
        screens,
        path,
        steps,
        selections: effectiveSelections,
        labels: dcrScreenLabels(screens),
    }), [screens, path, steps, effectiveSelections]);
    const setSelection = (stepId, value) => setEdits((current) => ({
        signature,
        values: { ...(current.signature === signature ? current.values : {}), [stepId]: value },
    }));
    return {
        /** The flow for a branching screen has loaded, so an impact can be worked out at all */
        isActive: steps.length > 0,
        /**
         * The picked screen branches. Known before the activity loads, so the form can hold back the
         * impacted section rather than showing one it is about to replace.
         */
        isBranchingScreen,
        isLoading: isBranchingScreen && query.isFetching,
        /** A rule the connector could not read, so the impacted screens are worth a second look */
        hasUnreadableRules: !!flow?.hasUnreadableRules,
        /** Every screen holding an answer, in activity order, whether or not it is on the path */
        steps,
        /** Only the screens the activity reaches with the answers currently picked */
        path,
        pathStepIds,
        recordedPathStepIds,
        selections: effectiveSelections,
        /** Screen titles keyed by step id, resolved against the answers currently selected */
        labels,
        setSelection,
        impact,
        /** The proposal moves the path, so the request has to describe more than the one screen */
        hasDiff,
        unansweredSteps: findUnansweredBranchingSteps(impact.newlyReachableSteps, effectiveSelections),
        changes: () => buildBranchingChanges(steps, path, effectiveSelections, labels),
    };
};
